// src/features/records/components/RecordDetail/components/RecordAccessIndicator.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Globe, Users, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { sanitizeText } from '../../../../../utils/sanitize';
import { Avatar, AvatarSkeleton, AVATAR_VARIANTS } from '../../../../../components/avatars';
import { useFloatingMenu } from '../../../../../hooks/useFloatingMenu';
import accessControlService from '../../../services/accessControlService';
import { VISIBILITY } from '../../../constants';
import { getGroupById } from '../../../../groups';
import useUserProfiles from '../../../hooks/useUserProfiles';
import styles from '../RecordDetail.module.css';

const useGroupSummaries = (groupIds) => {
  const [groupsById, setGroupsById] = useState({});
  const [loading, setLoading] = useState(false);
  const groupsRef = useRef(groupsById);

  useEffect(() => {
    groupsRef.current = groupsById;
  }, [groupsById]);

  const normalizedGroupIds = useMemo(() => {
    const seen = new Set();
    const ids = [];
    (groupIds || []).forEach((id) => {
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
    return ids;
  }, [groupIds]);

  const groupIdsKey = useMemo(
    () => (normalizedGroupIds.length ? normalizedGroupIds.join('|') : ''),
    [normalizedGroupIds]
  );

  useEffect(() => {
    const ids = groupIdsKey ? groupIdsKey.split('|').filter(Boolean) : [];

    if (ids.length === 0) {
      if (Object.keys(groupsRef.current).length > 0) {
        setGroupsById({});
      }
      setLoading(false);
      return;
    }

    const missing = ids.filter((id) => !groupsRef.current[id]);
    if (missing.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const results = await Promise.all(
          missing.map(async (groupId) => {
            try {
              const group = await getGroupById(groupId);
              return group ? { ...group, id: groupId } : null;
            } catch (error) {
              console.error(
                'RecordAccessIndicator: failed to fetch group summary',
                groupId,
                error
              );
              return null;
            }
          })
        );

        if (cancelled) return;

        setGroupsById((previous) => {
          const next = { ...previous };
          results.forEach((group) => {
            if (group) {
              next[group.id] = group;
            }
          });
          return next;
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupIdsKey]);

  return { groupsById, loading };
};

const RecordAccessIndicator = ({ recordId, access }) => {
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [grants, setGrants] = useState([]);

  const visibility = access?.visibility || VISIBILITY.PRIVATE;
  const ownerId = access?.owner || null;

  // Initialize floating menu hook for popover
  const menu = useFloatingMenu({
    placement: 'bottom-start',
    sameWidth: false,   // let the panel be wider than the trigger if needed
    exactWidth: false,  // use minWidth/maxWidth instead of locking width
    minWidthPx: 280,
    maxHeight: 360,
  });

  const panelId = `shared-popover-${recordId}`;

  // Subscribe to admin status and grants (admins only)
  useEffect(() => {
    let unsub = null;
    let mounted = true;

    async function init() {
      try {
        // Minimal record shell for isUserAdmin
        const recordShell = { id: recordId, access };
        const admin = await accessControlService.isUserAdmin(recordShell, { forceRefresh: true });
        if (!mounted) return;
        setIsAdmin(admin);

        if (admin) {
          unsub = accessControlService.subscribeToGrants(recordId, (list) => {
            setGrants(Array.isArray(list) ? list : []);
          });
        } else {
          setGrants([]);
        }
      } catch (e) {
        console.error(
          'RecordAccessIndicator: failed to init grants/admin state',
          e
        );
        setIsAdmin(false);
        setGrants([]);
      }
    }

    if (recordId && ownerId) {
      init();
    } else {
      // No record context → show only visibility
      setIsAdmin(false);
      setGrants([]);
    }

    return () => {
      mounted = false;
      if (typeof unsub === 'function') unsub();
    };
  }, [recordId, ownerId, access]);

  // Compute admin-visible sharing summary from grants
  const { userPrincipalIds, groupPrincipalIds } = useMemo(() => {
    if (!isAdmin || !Array.isArray(grants)) {
      return { userPrincipalIds: [], groupPrincipalIds: [] };
    }
    const friendGroupId = ownerId ? `friends:${ownerId}` : null;
    const filtered = grants.filter((g) => {
      // ignore public grant (visibility already covers that)
      if (g.principalType === 'public') return false;
      // when visibility === friends, hide the virtual friends grant (it's represented by the label)
      if (
        visibility === VISIBILITY.FRIENDS &&
        g.principalType === 'group' &&
        g.principalId === friendGroupId
      ) {
        return false;
      }
      return true;
    });

    const users = filtered
      .filter((g) => g.principalType === 'user' && g.principalId !== ownerId)
      .map((g) => g.principalId);

    const groups = filtered
      .filter((g) => g.principalType === 'group')
      .map((g) => g.principalId);

    return { userPrincipalIds: users, groupPrincipalIds: groups };
  }, [isAdmin, grants, ownerId, visibility]);

  const sharedPreviewUserIds = userPrincipalIds.slice(0, 3);
  const {
    profiles: userProfilesById,
    loading: userProfilesLoading,
  } = useUserProfiles(userPrincipalIds);
  const { groupsById, loading: groupSummariesLoading } = useGroupSummaries(groupPrincipalIds);

  // Build label & styling
  const getVisibilityStyle = () => {
    switch (visibility) {
      case VISIBILITY.PUBLIC:
        return {
          icon: Globe,
          label: t('records.detail.access.public', 'Public'),
          className: styles.accessPublic
        };
      case VISIBILITY.FRIENDS:
        return {
          icon: Users,
          label: t('records.detail.access.friends', 'Friends'),
          className: styles.accessFriends,
        };
      case VISIBILITY.PRIVATE:
      default: {
        const shareCountUsers = userPrincipalIds.length;
        const shareCountGroups = groupPrincipalIds.length;

        if (isAdmin && shareCountUsers + shareCountGroups > 0) {
          const parts = [];
          if (shareCountUsers > 0) {
            parts.push(
              t('records.detail.access.shared_users_count', {
                count: shareCountUsers,
                defaultValue_one: '{{count}} user',
                defaultValue_other: '{{count}} users'
              })
            );
          }
          if (shareCountGroups > 0) {
            parts.push(
              t('records.detail.access.shared_groups_count', {
                count: shareCountGroups,
                defaultValue_one: '{{count}} group',
                defaultValue_other: '{{count}} groups'
              })
            );
          }
          return {
            icon: Lock,
            label: t('records.detail.access.shared_label', {
              parts: parts.join(', '),
              defaultValue: 'Shared ({{parts}})'
            }),
            className: styles.accessPrivate,
          };
        }
        return {
          icon: Lock,
          label: t('records.detail.access.private', 'Private'),
          className: styles.accessPrivate,
        };
      }
    }
  };

  const { icon: Icon, label, className } = getVisibilityStyle();
  const isExpandable =
    visibility === VISIBILITY.PRIVATE &&
    isAdmin &&
    userPrincipalIds.length + groupPrincipalIds.length > 0;

  return (
    <div className={styles.recordAccessIndicatorWrapper}>
      <button
        ref={menu.refs.setReference}
        className={`${styles.recordAccessIndicator} ${className} ${
          isExpandable ? styles.expandable : ''
        }`}
        {...(isExpandable ? menu.referenceProps : {})}
        aria-haspopup={isExpandable ? "dialog" : undefined}
        aria-controls={isExpandable ? panelId : undefined}
        aria-expanded={isExpandable ? menu.open : undefined}
        onClick={isExpandable ? menu.referenceProps.onClick : undefined}
        disabled={!isExpandable}
        aria-label={isExpandable
          ? t('records.detail.access.expand_details_aria', {
            label,
            defaultValue: '{{label}}, click to see details'
          })
          : label}
        type="button"
        title={label}
      >
        <Icon className={styles.accessIcon} size={16} />
        <span className={styles.accessLabel}>
          <span className={styles.accessLabelText}>{label}</span>
          <span className={styles.accessLabelCompact}>
            {visibility === VISIBILITY.PUBLIC ? t('records.detail.access.public', 'Public') :
             visibility === VISIBILITY.FRIENDS ? t('records.detail.access.friends', 'Friends') :
             isAdmin && userPrincipalIds.length + groupPrincipalIds.length > 0 ?
             `${userPrincipalIds.length + groupPrincipalIds.length}` :
             t('records.detail.access.private', 'Private')}
          </span>
        </span>

        {/* Admin-only avatar previews for direct user grants */}
        {visibility === VISIBILITY.PRIVATE &&
          isAdmin &&
          userPrincipalIds.length > 0 &&
          !menu.open && (
            <div className={styles.sharedAvatars}>
              {sharedPreviewUserIds.map((userId, index) => (
                <SharedAvatar
                  key={userId}
                  userId={userId}
                  index={index}
                  profile={userProfilesById[userId]}
                  isLoading={userProfilesLoading && !userProfilesById[userId]}
                />
              ))}
              {userPrincipalIds.length > 3 && (
                <span className={styles.moreCount}>
                  +{userPrincipalIds.length - 3}
                </span>
              )}
            </div>
          )}

        {isExpandable && (
          <div className={styles.accessChevron}>
            {menu.open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
        )}
      </button>

      {/* Floating panel (admin only) */}
      {isExpandable && menu.open && (
        <div
          ref={menu.refs.setFloating}
          {...menu.floatingProps}
          className={styles.sharedPopoverWrapper}
          data-align="start"
        >
          <div 
            id={panelId}
            role="dialog"
            aria-modal={false}
            aria-label={t('records.detail.access.shared_with_aria', 'Shared with')}
            className={styles.sharedPopover}
          >
            <div className={styles.sharedUsersList}>
              {userPrincipalIds.map((userId) => (
                <SharedUserRow
                  key={userId}
                  userId={userId}
                  profile={userProfilesById[userId]}
                  isLoading={userProfilesLoading && !userProfilesById[userId]}
                />
              ))}

              {groupPrincipalIds.map((groupId) => (
                <SharedGroupRow
                  key={groupId}
                  groupId={groupId}
                  group={groupsById[groupId]}
                  isLoading={groupSummariesLoading && !groupsById[groupId]}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Avatar component for compact display
const SharedAvatar = ({ userId, index, profile, isLoading }) => {
  const { t } = useTranslation();
  if (!profile && isLoading) {
    return (
      <AvatarSkeleton
        size="xs"
        className={styles.sharedAvatar}
        style={{ zIndex: 3 - index }}
      />
    );
  }

  const fallbackName = sanitizeText(
    profile?.displayName || userId || t('records.detail.access.shared_user_fallback', 'Shared user')
  ) || t('records.detail.access.shared_user_fallback', 'Shared user');

  if (!profile) {
    return (
      <Avatar
        name={fallbackName}
        size="xs"
        className={styles.sharedAvatar}
        style={{ zIndex: 3 - index }}
        alt={t('records.detail.access.avatar_alt', {
          name: fallbackName,
          defaultValue: '{{name}} avatar'
        })}
      />
    );
  }

  const displayName = profile.displayName || fallbackName;

  return (
    <Avatar
      name={displayName}
      src={profile?.profilePictureUrl}
      size="xs"
      className={styles.sharedAvatar}
      style={{ zIndex: 3 - index }}
      alt={t('records.detail.access.avatar_alt', {
        name: displayName,
        defaultValue: '{{name}} avatar'
      })}
    />
  );
};

SharedAvatar.propTypes = {
  userId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  profile: PropTypes.shape({
    displayName: PropTypes.string,
    profilePictureUrl: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

SharedAvatar.defaultProps = {
  profile: undefined,
  isLoading: false,
};

// Full user row for expanded view
const SharedUserRow = ({ userId, profile, isLoading }) => {
  const { t } = useTranslation();
  if (!profile && isLoading) {
    return (
      <div className={styles.sharedUserRow}>
        <AvatarSkeleton size="sm" className={styles.sharedUserAvatar} />
        <div className={styles.sharedUserInfo}>
          <div className={styles.loadingPulse} style={{ width: '120px', height: '14px' }} />
        </div>
      </div>
    );
  }

  if (!profile) {
    const fallbackName = sanitizeText(userId) || userId || '—';
    return (
      <div className={styles.sharedUserRow}>
        <Avatar
          name={fallbackName}
          size="sm"
          className={styles.sharedUserAvatar}
          alt={t('records.detail.access.avatar_alt', {
            name: fallbackName,
            defaultValue: '{{name}} avatar'
          })}
        />
        <div className={styles.sharedUserInfo}>
          <span className={styles.sharedUserName}>{fallbackName}</span>
        </div>
      </div>
    );
  }

  const fallbackName = sanitizeText(profile.displayName || profile.username || userId)
    || sanitizeText(userId)
    || userId
    || '—';
  const displayName = fallbackName;

  return (
    <div className={styles.sharedUserRow}>
      <Avatar
        name={displayName}
      src={profile.profilePictureUrl}
      size="sm"
      className={styles.sharedUserAvatar}
      alt={t('records.detail.access.avatar_alt', {
        name: displayName,
        defaultValue: '{{name}} avatar'
      })}
    />
      <div className={styles.sharedUserInfo}>
        <span className={styles.sharedUserName}>
          {displayName}
        </span>
        {profile.username && (
          <span className={styles.sharedUserUsername}>
            @{sanitizeText(profile.username)}
          </span>
        )}
      </div>
    </div>
  );
};

SharedUserRow.propTypes = {
  userId: PropTypes.string.isRequired,
  profile: PropTypes.shape({
    displayName: PropTypes.string,
    username: PropTypes.string,
    profilePictureUrl: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

SharedUserRow.defaultProps = {
  profile: undefined,
  isLoading: false,
};

// Group row for expanded view (no PII beyond group summary)
const SharedGroupRow = ({ groupId, group, isLoading }) => {
  const { t } = useTranslation();
  if (isLoading && !group) {
    return (
      <div className={styles.sharedUserRow}>
        <AvatarSkeleton size="sm" className={styles.sharedUserAvatar} />
        <div className={styles.sharedUserInfo}>
          <div
            className={styles.loadingPulse}
            style={{ width: '120px', height: '14px' }}
          />
        </div>
      </div>
    );
  }

  const rawLabel = group?.name || groupId;
  const displayLabel = sanitizeText(rawLabel) || t('records.detail.access.group_fallback', 'Group');

  return (
    <div className={styles.sharedUserRow}>
      <Avatar
        variant={AVATAR_VARIANTS.GROUP}
        name={rawLabel}
        src={group?.icon}
        size="sm"
        className={styles.sharedUserAvatar}
        alt={t('records.detail.access.group_avatar_alt', {
          name: displayLabel,
          defaultValue: '{{name}} group avatar'
        })}
      />
      <div className={styles.sharedUserInfo}>
        <span className={styles.sharedUserName}>{displayLabel}</span>
        <span className={styles.sharedUserUsername}>
          {t('records.detail.access.group_label', 'group')}
        </span>
      </div>
    </div>
  );
};

SharedGroupRow.propTypes = {
  groupId: PropTypes.string.isRequired,
  group: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    icon: PropTypes.string,
    memberCount: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
};

SharedGroupRow.defaultProps = {
  group: undefined,
  isLoading: false,
};

RecordAccessIndicator.propTypes = {
  recordId: PropTypes.string.isRequired, // NEW: needed to read grants
  access: PropTypes.shape({
    owner: PropTypes.string.isRequired,
    visibility: PropTypes.oneOf(['private', 'friends', 'public']).isRequired
  }).isRequired
};

export default RecordAccessIndicator;
